/* eslint-disable no-nested-ternary */
import { Check, Link2, Plus } from "@langfuse/icons";
import { type ReactNode, useState } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { TextActionButton } from "@/src/components/TextActionButton/TextActionButton";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { EvaluatorMappingRow } from "@/src/features/evals/v2/components/Rules/EvaluatorMappingRow/EvaluatorMappingRow";
import type {
  RuleEvaluatorOption,
  RuleSetupStore,
} from "@/src/features/evals/v2/types/rules";

import { EvaluatorPickerOption } from "@/src/features/evals/v2/components/Rules/EvaluatorAssignmentsEditor/components/EvaluatorPickerOption/EvaluatorPickerOption";
import type { RuleCostEstimate } from "@/src/features/evals/v2/hooks/useRuleCostEstimate";
import { RuleEvaluatorCostEstimate } from "@/src/features/evals/v2/components/Rules/RuleSetup/components/RuleEvaluatorCostEstimate";
import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/utils/tailwind";

export function EvaluatorAssignmentsEditor({
  evaluatorOptions,
  store,
  search,
  onSearchChange,
  sampleObject,
  unvalidatedSourceColumnIds,
  emptyActionLabel = "Attach evaluator",
  additionalActionLabel = "Attach another evaluator",
  emptyDescription = "Attach an evaluator to run on matching observations.",
  sourceUnavailableMessage,
  pickerWidth = "fixed",
  disabled = false,
  costEstimates,
  estimatingEvaluatorIds,
  footerTrailing,
}: {
  evaluatorOptions: RuleEvaluatorOption[];
  store: RuleSetupStore;
  search: string;
  onSearchChange: (search: string) => void;
  sampleObject: Record<string, unknown> | null;
  unvalidatedSourceColumnIds?: string[];
  emptyActionLabel?: string;
  additionalActionLabel?: string;
  emptyDescription?: string;
  sourceUnavailableMessage?: string;
  pickerWidth?: "fixed" | "trigger";
  disabled?: boolean;
  costEstimates: RuleCostEstimate[];
  estimatingEvaluatorIds: string[];
  footerTrailing: ReactNode;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const assignments = useStore(store, (state) => state.assignments);
  const attachedIds = useStore(
    store,
    useShallow((state) =>
      state.assignments.map((assignment) => assignment.evaluatorId),
    ),
  );
  const attachEvaluator = useStore(
    store,
    (state) => state.actions.attachEvaluator,
  );
  const attachedIdSet = new Set(attachedIds);
  const attached = evaluatorOptions.filter((evaluator) =>
    attachedIdSet.has(evaluator.id),
  );
  const available = evaluatorOptions.filter(
    (evaluator) => !attachedIdSet.has(evaluator.id),
  );
  const evaluatorPicker = (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
      <PopoverTrigger asChild>
        {attachedIds.length === 0 ? (
          <button
            type="button"
            disabled={disabled}
            className="border-border hover:bg-muted/50 focus-visible:ring-ring flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed px-4 py-6 text-center transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex items-center gap-2 text-sm font-bold">
              <Link2 size="base" />
              {emptyActionLabel}
            </span>
            <span className="text-muted-foreground text-sm font-normal">
              {emptyDescription}
            </span>
          </button>
        ) : (
          <TextActionButton
            text={additionalActionLabel}
            disabled={disabled}
            width={pickerWidth === "trigger" ? "fill" : "content"}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "ph-no-capture h-80 max-w-[calc(100vw-2rem)] p-0",
          pickerWidth === "trigger"
            ? "w-(--radix-popover-trigger-width)"
            : "w-[32rem]",
        )}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Find an evaluator..."
            value={search}
            onValueChange={onSearchChange}
          />
          <CommandList className="min-h-0 flex-1">
            <CommandEmpty>No evaluator found.</CommandEmpty>
            {attached.length > 0 ? (
              <CommandGroup heading="Already attached">
                {attached.map((evaluator) => (
                  <CommandItem
                    key={evaluator.id}
                    value={`${evaluator.name} ${evaluator.id} already attached`}
                    disabled
                    className="py-2.5"
                  >
                    <Check size="base" />
                    <EvaluatorPickerOption evaluator={evaluator} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {available.length > 0 ? (
              <CommandGroup heading="Available evaluators">
                {available.map((evaluator) => (
                  <CommandItem
                    key={evaluator.id}
                    value={`${evaluator.name} ${evaluator.id}`}
                    className="py-2.5"
                    onSelect={() => {
                      attachEvaluator({
                        evaluatorId: evaluator.id,
                        evaluatorName: evaluator.name,
                        evaluatorType: evaluator.type,
                        defaultVariableMapping:
                          evaluator.defaultVariableMapping,
                        variableMapping: evaluator.initialVariableMapping,
                        requiredVariables: evaluator.requiredVariables,
                      });
                      setPickerOpen(false);
                    }}
                  >
                    <Plus size="base" />
                    <EvaluatorPickerOption evaluator={evaluator} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="space-y-3">
      {attachedIds.length === 0 ? (
        evaluatorPicker
      ) : (
        <>
          <ul className="overflow-hidden rounded-md border">
            {attachedIds.map((evaluatorId) => {
              const evaluator = evaluatorOptions.find(
                (option) => option.id === evaluatorId,
              );
              const assignment = assignments.find(
                (candidate) => candidate.evaluatorId === evaluatorId,
              );
              const costEstimate = costEstimates.find(
                (estimate) => estimate.evaluatorId === evaluatorId,
              );

              return (
                <EvaluatorMappingRow
                  key={evaluatorId}
                  evaluatorId={evaluatorId}
                  evaluatorName={
                    assignment?.evaluatorName ?? evaluator?.name ?? "Evaluator"
                  }
                  evaluatorType={
                    assignment?.evaluatorType ??
                    evaluator?.type ??
                    "LLM_AS_JUDGE"
                  }
                  defaultVariableMapping={
                    assignment?.defaultVariableMapping ??
                    evaluator?.defaultVariableMapping ??
                    []
                  }
                  store={store}
                  sampleObject={sampleObject}
                  unvalidatedSourceColumnIds={unvalidatedSourceColumnIds}
                  sourceUnavailableMessage={sourceUnavailableMessage}
                  disabled={disabled}
                  costEstimate={
                    estimatingEvaluatorIds.includes(evaluatorId) ? (
                      <Skeleton className="h-4 w-28" />
                    ) : costEstimate ? (
                      <RuleEvaluatorCostEstimate estimate={costEstimate} />
                    ) : null
                  }
                />
              );
            })}
          </ul>
          <div className="flex items-start justify-between gap-3">
            {evaluatorPicker}
            {footerTrailing}
          </div>
        </>
      )}
    </div>
  );
}
